import { Link, useParams } from 'react-router-dom';

import { AskKim } from './components/ask-kim';
import { DocProcessingInfo } from './components/doc-processing-info';
import { MatterOverviewLoader } from './components/matter-overview-loader';
import { MatterStatCard } from './components/matter-stat-card';
import { RecentActivity } from './components/recent-activity';
import { RecentSubsets } from './components/recent-subsets';
import { SuggestedSubset } from './components/suggested-subset';
import { useGetCase } from '@/api/queries/useGetCase';
import useGetCaseOverview from '@/api/queries/useGetCaseOverview';
import { useGetDocSubsets } from '@/api/queries/useGetDocSubsets';
import { useGetPersons } from '@/api/queries/useGetPersons';
import formatDate from '@/helpers/formatDate';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';

export function MatterOverview() {
  const { matterId } = useParams();
  if (!matterId) return;

  const { data: matter, isLoading: isLoadingCase } = useGetCase(matterId);
  const { data: overview, isLoading: isLoadingOverview } = useGetCaseOverview(matterId);
  const { data: subsets, isLoading: isLoadingSubsets } = useGetDocSubsets(matterId);
  const { data: personsData, isLoading: isLoadingPersons } = useGetPersons({
    caseId: matterId,
    paginated: true,
    page: 1,
    pageSize: 1,
  });

  const isLoading = isLoadingCase || isLoadingOverview || isLoadingSubsets || isLoadingPersons;

  // Get all documents subset and recent subsets (excluding all documents)
  const allDocumentsSubset = subsets?.find((s) => s.isAllDocuments);
  const recentSubsets = subsets?.filter((s) => !s.isAllDocuments).slice(0, 3) ?? [];

  if (isLoading) {
    return <MatterOverviewLoader />;
  }

  return (
    <>
      <Breadcrumb
        items={
          matter
            ? [
                { href: '/matters', label: 'Matters', active: false },
                {
                  href: `/matters/${matter.id}`,
                  label: matter.name,
                  active: true,
                },
              ]
            : [{ href: '/matters', label: 'Matters', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle text={matter?.name ?? ''} subtext={`Created: ${formatDate(matter?.createdDate ?? '')}`} />

          <div className="flex items-center gap-2">
            <Button asChild className="py-1">
              <Link to={`/matters/${matterId}/settings`}>
                <Icon className="lucide--users-round" />
                Users {matter?.users?.length}
              </Link>
            </Button>

            <Button asChild className="py-1">
              <Link to={`/matters/${matterId}/settings`}>
                <Icon className="lucide--settings" />
                Matter Settings
              </Link>
            </Button>
          </div>
        </PageHeader>

        <div className="mb-6 grid grid-cols-1 gap-3 lg:grid-cols-5">
          <div className="grid grid-cols-4 gap-3 lg:col-span-3">
            <MatterStatCard
              icon="lucide--file-text"
              label="Documents"
              value={overview?.documents?.toLocaleString() ?? '-'}
            />
            <MatterStatCard icon="lucide--search" label="Facts" value={overview?.totalFacts?.toLocaleString() ?? '-'} />
            <MatterStatCard
              icon="lucide--users-round"
              label="Entities"
              value={personsData?.totalCount?.toLocaleString() ?? '-'}
            />
            <MatterStatCard
              icon="lucide--alert-triangle"
              label="Inconsistencies"
              value={overview?.disputedFacts?.toLocaleString() ?? '-'}
            />
          </div>

          <DocProcessingInfo allDocumentsSubsetId={allDocumentsSubset?.id} />
        </div>

        <div className="mb-2 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <RecentSubsets recentSubsets={recentSubsets} />
            <SuggestedSubset />
          </div>

          <div className="lg:col-span-1">
            <AskKim allDocumentsSubsetId={allDocumentsSubset?.id ?? ''} />
            <RecentActivity allDocumentsSubsetId={allDocumentsSubset?.id ?? ''} />
          </div>
        </div>
      </PageContainer>
    </>
  );
}
